import { useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router'
import { useAppSelector } from '../../hooks/useStore'
// eslint-disable-next-line import/extensions
import Platform, { PLATFORMS } from '../../platforms'
// eslint-disable-next-line import/extensions
import tts from '../../lib/TTS'
// eslint-disable-next-line import/extensions
import googleAnalytics4 from '../../lib/googleAnalytics'
// eslint-disable-next-line import/extensions
import * as UI from '../../lib/ui'
import { truncateStationName } from './utils/truncateStationName'
import type { PassportHandoffHookReturn } from './types'

const FALLBACK_DISPLAY_URL = 'passport.pbs.org'
const QR_BASE_URL = 'https://www.pbs.org/donation/'

const PLATFORM_CAMPAIGN_MAP: Record<string, string> = {
  [PLATFORMS.SAMSUNG]: 'passport_samsungtv',
  [PLATFORMS.VIZIO]: 'passport_vizio',
  [PLATFORMS.COMCAST]: 'passport_comcast',
  [PLATFORMS.XUMO]: 'passport_comcast',
  [PLATFORMS.LG]: 'passport_lgtv',
  [PLATFORMS.BROWSER]: 'passport_browser',
}

const PLATFORM_UTM_MEDIUM_MAP: Record<string, string> = {
  [PLATFORMS.SAMSUNG]: 'samsungtv',
  [PLATFORMS.VIZIO]: 'vizio',
  [PLATFORMS.COMCAST]: 'comcast',
  [PLATFORMS.XUMO]: 'comcast',
  [PLATFORMS.LG]: 'lgtv',
  [PLATFORMS.BROWSER]: 'browser',
}

export default function usePassportHandoffScreen(): PassportHandoffHookReturn {
  const station = useAppSelector((state) => state.station)
  const memberships = useAppSelector((state) => state.user.memberships)
  const deeplinkedTo = useAppSelector((state) => state.deeplink?.deeplinkedTo)

  const params = useParams<{ callsign: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  const isMembership = location.search.includes('membership=true')
  const isVideoUpsell = location.search.includes('?video=')
  const goBackUrl = (location.state as any)?.goBackParameterUrl

  const resolvedStation = useMemo(() => {
    if (isMembership && params.callsign) {
      const match = memberships.find(
        (m: any) => m.call_sign === params.callsign
      )
      if (match) return match
    }
    return station
  }, [isMembership, params.callsign, memberships, station])

  const shortCommonName = resolvedStation.short_common_name || ''
  const stationDisplayName = truncateStationName(shortCommonName)

  const displayUrl = useMemo(() => {
    const url = resolvedStation.passport_learn_more_url
    if (!url) return FALLBACK_DISPLAY_URL
    return url.replace('https://', '').replace('http://', '')
  }, [resolvedStation])

  const qrUrl = useMemo(() => {
    const stationId = resolvedStation.station_id
    const platformName = Platform.getPlatformName()
    const campaign = PLATFORM_CAMPAIGN_MAP[platformName] || 'passport_samsungtv'
    const utmMedium = PLATFORM_UTM_MEDIUM_MAP[platformName] || 'samsungtv'

    if (!stationId) return ''

    const queryParams = new URLSearchParams({
      station_id: stationId,
      campaign,
      utm_source: 'ctv_app',
      utm_medium: utmMedium,
      utm_campaign: 'passport_handoff_qrcode',
    })

    return `${QR_BASE_URL}?${queryParams.toString()}`
  }, [resolvedStation])

  const isQrUrlValid = useMemo(
    () => qrUrl !== '' && resolvedStation.station_id !== '',
    [qrUrl, resolvedStation]
  )

  const handleGoBack = useCallback(() => {
    tts.stopRead()
    if (goBackUrl) {
      const hasDeeplinkOrigin = deeplinkedTo !== false && deeplinkedTo !== ''
      const detailsUrl = `/video-details/${goBackUrl}`
      if (hasDeeplinkOrigin) {
        setTimeout(() => {
          navigate(detailsUrl, { state: { origin: deeplinkedTo, fromPassportUpsell: true } })
        }, 0)
      } else {
        navigate(detailsUrl, { state: { fromPassportUpsell: true } })
      }
    } else {
      navigate(-1)
    }
  }, [goBackUrl, deeplinkedTo, navigate])

  const handleWhatIsPassport = useCallback(() => {
    tts.stopRead()
    navigate('/what-is-passport', { state: { displayUrl, stationDisplayName } })
  }, [navigate, displayUrl, stationDisplayName])

  useEffect(() => {
    if (!isQrUrlValid) return undefined

    UI.setSidePanelVisibility(false)

    googleAnalytics4.sendPageViewEvent('Passport Handoff')

    tts.pushTextToRead(
      `${stationDisplayName} Passport. Donate to become a member of ${stationDisplayName} and access Passport. Passport is a member benefit that lets you stream more of your favorite PBS shows. Donate and Start Watching. Visit ${displayUrl}. Or scan the QR code with your device's camera.`
    )

    return () => {
      tts.stopRead()
    }
  }, [stationDisplayName, displayUrl, isQrUrlValid])

  return {
    stationDisplayName,
    qrUrl,
    displayUrl,
    isVideoUpsell,
    isQrUrlValid,
    handleGoBack,
    handleWhatIsPassport,
  }
}
